import React, { useEffect, useCallback, useRef } from "react";
import { msalInstance } from "../msalInstance";

export type PickedFile = {
  id: string;
  driveId: string;
  webUrl: string;
  name: string;
  size?: number;
};

type Props = {
  onPicked: (files: PickedFile[]) => void;
  label?: string;
  multiSelect?: boolean;
};

const OD_BUSINESS_ENDPOINT_HINT =
  process.env.REACT_APP_SHAREPOINT_HINT || "https://leoberwick-my.sharepoint.com/";

export default function SharePointFilePickerButton({
  onPicked,
  label = "📁 Select from SharePoint",
  multiSelect = true,
}: Props) {
  useEffect(() => {
    const od = (window as any).OneDrive;
    if (!od) {
      const s = document.createElement("script");
      s.src = "https://js.live.net/v7.2/OneDrive.js";
      s.async = true;
      s.crossOrigin = "anonymous";
      document.body.appendChild(s);
    }
  }, []);

  const isOpeningRef = useRef(false);

  const openPicker = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (isOpeningRef.current) return;

      const OneDrive = (window as any).OneDrive;
      if (!OneDrive) {
        console.error("OneDrive.js not loaded yet");
        return;
      }

      isOpeningRef.current = true;
      const redirectUri = `${window.location.origin}/onedrive-redirect.html`;

      OneDrive.open({
        clientId: msalInstance.getConfiguration().auth.clientId,
        action: "query",
        viewType: "all",
        multiSelect,
        openInNewWindow: true,
        advanced: {
          redirectUri,
          endpointHint: OD_BUSINESS_ENDPOINT_HINT,
          isConsumerAccount: false,
        },
        success: (res: any) => {
          isOpeningRef.current = false;
          const items = (res?.value || []).filter((it: any) => !it.folder);
          const picked: PickedFile[] = items.map((it: any) => ({
            id: it.id,
            driveId: it.parentReference?.driveId ?? it.parentReference?.id ?? "",
            webUrl: it.webUrl,
            name: it.name,
            size: it.size,
          }));
          if (picked.length > 0) onPicked(picked);
        },
        cancel: () => {
          isOpeningRef.current = false;
        },
        error: (err: any) => {
          isOpeningRef.current = false;
          console.error("OneDrive picker error:", err);
        },
      });
    },
    [onPicked, multiSelect],
  );

  return (
    <button
      type="button"
      onClick={openPicker}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid var(--theme-border)",
        background: "#ffffff",
        color: "var(--theme-ink)",
        cursor: "pointer",
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      {label}
    </button>
  );
}
